import { create } from '@fhs/zustand';

export type TipSelection = '15' | '18' | '20' | 'other';

const DEFAULT_TIP = '18' as const;
const TIP_OPTIONS = [15, 18, 20] as const;
const MAX_TIP_AMOUNT_CENTS = 6300;

interface TipState {
  totalTipCentsForCommit: number;
  selectedTip: TipSelection;
  otherTipValue: string;
  // tipSelection needs to be string also to be used with MonetaryAmountOptionSelector component
  setSelectedTip: (tip: string) => void;
  setOtherTipValue: (value: string) => void;
  resetTip: () => void;
  setTotalTipCentsForCommit: (tipCents: number) => void;

  calculateTipCents: (subTotalCents: number, discountAmount?: number) => number;
  validateTip: (
    subTotalCents: number,
    discountAmount?: number
  ) => {
    isValid: boolean;
    errorMessage?: string;
  };
}

export const useTipStore = create<TipState>((set, get) => ({
  selectedTip: DEFAULT_TIP,
  otherTipValue: '',
  totalTipCentsForCommit: 0,
  setSelectedTip: (tip: string) => {
    set({ selectedTip: tip as TipSelection });
    // Keep otherTipValue so user's custom amount persists when switching between options
  },

  setOtherTipValue: (value: string) => {
    set({ otherTipValue: value });
  },

  resetTip: () => {
    set({
      selectedTip: DEFAULT_TIP,
      otherTipValue: '',
      totalTipCentsForCommit: 0,
    });
  },

  setTotalTipCentsForCommit: (tipCents: number) => {
    set({ totalTipCentsForCommit: tipCents });
  },

  calculateTipCents: (subTotalCents: number, discountAmount: number = 0): number => {
    const state = get();

    if (state.selectedTip === 'other') {
      const customTipCents = parseInt(state.otherTipValue || '0', 10);
      const tipValue = isNaN(customTipCents) ? 0 : customTipCents;
      set({ totalTipCentsForCommit: tipValue });
      return tipValue;
    }

    const baseAmount = subTotalCents + discountAmount;
    const percentage = parseInt(state.selectedTip, 10);
    const tipCents = Math.round((baseAmount * percentage) / 100);
    set({ totalTipCentsForCommit: tipCents });
    return tipCents;
  },

  validateTip: (subTotalCents: number, discountAmount: number = 0) => {
    const state = get();
    const tipCents = get().calculateTipCents(subTotalCents, discountAmount);

    if (state.selectedTip !== 'other') {
      return { isValid: true };
    }

    if (tipCents > MAX_TIP_AMOUNT_CENTS) {
      return {
        isValid: false,
        errorMessage: `Tip cannot exceed $${(MAX_TIP_AMOUNT_CENTS / 100).toFixed(2)}`,
      };
    }

    return { isValid: true };
  },
}));

export { TIP_OPTIONS, MAX_TIP_AMOUNT_CENTS, DEFAULT_TIP };
